import { c as _c } from "react/compiler-runtime";
import { IcoChevronRight16, IcoFileText16, IcoFolders16 } from '@onefootprint/icons';
import type { SkillFileNode } from '@onefootprint/request-types/dashboard';
import { cx } from 'class-variance-authority';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSkillsSave } from '../../hooks/use-skills-save';
import { useBuilder } from '../../store/provider';
import { useDropTarget } from './hooks/use-drop-target';
import SkillActions from './skill-actions';
type SkillNodeProps = {
  node: SkillFileNode;
  path: string;
  depth: number;
};
const SkillNode = t0 => {
  const $ = _c(62);
  const {
    node,
    path,
    depth
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.skills"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const save = useSkillsSave();
  const {
    dropTarget,
    setDropTarget
  } = useDropTarget();
  const [expanded, setExpanded] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const rowRef = useRef(null);
  const inputRef = useRef(null);
  const isFolder = node.kind === "folder";
  const selected = state.selectedSkillPath === path;
  const isTarget = dropTarget?.path === path;
  let t2;
  if ($[1] !== isFolder || $[2] !== node.name || $[3] !== t) {
    t2 = node.name || (isFolder ? t(_temp) : t(_temp2));
    $[1] = isFolder;
    $[2] = node.name;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const name = t2;
  let t3;
  let t4;
  if ($[5] !== renaming) {
    t3 = () => {
      if (renaming) {
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    t4 = [renaming];
    $[5] = renaming;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== actions || $[9] !== isFolder || $[10] !== isReadOnly || $[11] !== path || $[12] !== setDropTarget) {
    t5 = event => {
      if (isReadOnly || !event.dataTransfer.types.includes("Files")) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rect = rowRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const before = (event.clientY - rect.top) / rect.height < 0.5;
      const position = before ? "before" : isFolder ? "inside" : "after";
      setDropTarget({
        path,
        position,
        onDrop: (nodes, dropPosition) => actions.skills.dropExternal(path, dropPosition, nodes)
      });
    };
    $[8] = actions;
    $[9] = isFolder;
    $[10] = isReadOnly;
    $[11] = path;
    $[12] = setDropTarget;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const onDragOver = t5;
  let t6;
  if ($[14] !== dropTarget || $[15] !== isTarget) {
    t6 = isTarget && dropTarget.position === "before" && <div className="absolute inset-x-0 top-0 h-0.5 bg-accent" />;
    $[14] = dropTarget;
    $[15] = isTarget;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = !selected;
  const t8 = isTarget && dropTarget.position === "inside";
  let t9;
  if ($[17] !== selected || $[18] !== t7 || $[19] !== t8) {
    t9 = cx("flex min-w-0 items-center gap-1 rounded-md pr-1", {
      "bg-senary/70 text-primary": selected,
      "text-secondary hover:bg-hover": t7,
      "ring-1 ring-accent": t8
    });
    $[17] = selected;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = `${depth * 16 + 4}px`;
  let t11;
  if ($[21] !== t10) {
    t11 = {
      paddingLeft: t10
    };
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== actions || $[24] !== expanded || $[25] !== isFolder || $[26] !== name || $[27] !== node.name || $[28] !== path || $[29] !== renaming || $[30] !== save || $[31] !== selected || $[32] !== t) {
    t12 = renaming ? <input ref={inputRef} aria-label={t(_temp3)} value={node.name} onChange={event_0 => actions.skills.rename(path, event_0.target.value)} onBlur={() => {
      save();
      setRenaming(false);
    }} onKeyDown={_temp4} className="min-w-0 flex-1 rounded border border-accent bg-primary px-2 py-1 text-body-2 outline-none" /> : <button type="button" onClick={() => isFolder ? setExpanded(!expanded) : actions.skills.select(path)} aria-current={selected ? "page" : undefined} aria-expanded={isFolder ? expanded : undefined} title={name} className="flex min-w-0 flex-1 items-center gap-2 h-9 rounded pl-1 text-left text-body-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">{isFolder ? <IcoChevronRight16 className={cx("shrink-0", {
        "rotate-90": expanded
      })} /> : <IcoFileText16 className="shrink-0 text-tertiary" />}{isFolder && <IcoFolders16 className="shrink-0 text-tertiary" />}<span className="truncate">{name}</span></button>;
    $[23] = actions;
    $[24] = expanded;
    $[25] = isFolder;
    $[26] = name;
    $[27] = node.name;
    $[28] = path;
    $[29] = renaming;
    $[30] = save;
    $[31] = selected;
    $[32] = t;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== node.name) {
    t13 = node.name === "SKILL.md" ? undefined : () => setRenaming(true);
    $[34] = node.name;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== node || $[37] !== path || $[38] !== t13) {
    t14 = <SkillActions node={node} path={path} onRename={t13} />;
    $[36] = node;
    $[37] = path;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t14 || $[43] !== t9) {
    t15 = <div className={t9} style={t11}>{t12}{t14}</div>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t14;
    $[43] = t9;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== dropTarget || $[46] !== isTarget) {
    t16 = isTarget && dropTarget.position === "after" && <div className="absolute inset-x-0 bottom-0 h-0.5 bg-accent" />;
    $[45] = dropTarget;
    $[46] = isTarget;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== onDragOver || $[49] !== t15 || $[50] !== t16 || $[51] !== t6) {
    t17 = <div ref={rowRef} onDragOver={onDragOver} className="relative">{t6}{t15}{t16}</div>;
    $[48] = onDragOver;
    $[49] = t15;
    $[50] = t16;
    $[51] = t6;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== depth || $[54] !== expanded || $[55] !== node.children || $[56] !== node.kind || $[57] !== path) {
    t18 = node.kind === "folder" && expanded && node.children.map((child, index) => {
      const childPath = `${path}.${index}`;
      return <SkillNode key={childPath} node={child} path={childPath} depth={depth + 1} />;
    });
    $[53] = depth;
    $[54] = expanded;
    $[55] = node.children;
    $[56] = node.kind;
    $[57] = path;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== t17 || $[60] !== t18) {
    t19 = <div>{t17}{t18}</div>;
    $[59] = t17;
    $[60] = t18;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  return t19;
};
export { SkillNode };
function _temp($) {
  return $.untitled;
}
function _temp2($_0) {
  return $_0.newFile;
}
function _temp3($_1) {
  return $_1.fileName;
}
function _temp4(event_1) {
  if (event_1.key === "Enter" || event_1.key === "Escape") {
    event_1.currentTarget.blur();
  }
}